import { useTranslation } from 'react-i18next';
import { Setting, useGetGlobalSettingsQuery, useUpdateSettingsMutation, SETTING_TYPE, type AdvisorRegionSetting } from '@circuitry-ai/doc-data';
import { checkType, CtyShowToast, ErrorBoundary, SettingsIntegrationCard, SETTING_SCOPE, useHasApplicationResource, useTheme } from '@circuitry-ai/doc-ui';
import { useEffect, useRef, useState } from 'react';

export type SettingsIntegrationSection = 'behavior' | 'capabilities';

export interface SettingsIntegrationsProps {
    /** When set, card shows section-specific UI: behavior = sliders + View Integration; capabilities = switches + View Integration */
    readonly currentSection?: SettingsIntegrationSection;
}

export default function SettingsIntegrations(props: Readonly<SettingsIntegrationsProps>) {
    const { currentSection } = props;
    const { t } = useTranslation();
    const hasIntegrationsAccess = useHasApplicationResource('integrations');
    const { setDarkModeFeatureEnabled } = useTheme();
    const [updateSettings] = useUpdateSettingsMutation();
    const [integrationsData, setIntegrationsData] = useState<Setting[]>([]);

    const { data: settingsData, error: settingsError, isLoading: settingsLoading, isFetching: settingsFetching } = useGetGlobalSettingsQuery(undefined, { refetchOnMountOrArgChange: true });

    useEffect(() => {
        if (settingsData && !settingsLoading && !settingsError) {
            setIntegrationsData(
                settingsData.map((item) => {
                    const rawValue = item.ability_value ?? item.value ?? (item as { default_value?: number }).default_value;
                    const ability_value = rawValue === undefined || rawValue === null ? rawValue : Number(rawValue);
                    return { ...item, ability_value };
                }),
            );
        }
    }, [settingsData, settingsLoading, settingsError]);

    const countryTranslationSyncRef = useRef(false);
    useEffect(() => {
        if (!integrationsData.length) return;
        const country = integrationsData.find((entry) => entry.ability === SETTING_TYPE.COUNTRY);
        const translation = integrationsData.find((entry) => entry.ability === SETTING_TYPE.TRANSLATION);
        const hasConflict = Boolean(country?.is_active && translation?.is_active);
        if (!hasConflict) {
            countryTranslationSyncRef.current = false;
            return;
        }
        if (countryTranslationSyncRef.current) return;
        const translationIndex = integrationsData.findIndex((entry) => entry.ability === SETTING_TYPE.TRANSLATION);
        if (translationIndex === -1) return;
        countryTranslationSyncRef.current = true;
        void updateIntegration(translation, translationIndex, false);
    }, [integrationsData]);

    const updateIntegration = async (item: Setting, index: number, updateActive?: boolean) => {
        const ability = item.ability;
        const abilityKey = String(ability ?? '').toLowerCase();
        const isRum = abilityKey === SETTING_TYPE.RUM;
        const showSlider =
            abilityKey === SETTING_TYPE.DEPTH ||
            abilityKey === SETTING_TYPE.TEMPERATURE ||
            isRum;
        let defaultValue = 2;
        if (isRum) defaultValue = 0;
        else if (item.ability === SETTING_TYPE.TEMPERATURE) defaultValue = 0.5;
        const abilityValue = item?.value ?? item?.ability_value ?? defaultValue;
        const isActive = updateActive ?? !item.is_active;

        // Keep header theme gate in sync without invalidating Settings (avoids Capabilities remount/flicker).
        if (item.ability === SETTING_TYPE.DARK_MODE) {
            setDarkModeFeatureEnabled(isActive);
        }
        
        setIntegrationsData((prevData) => {
            const newData = [...prevData];
            newData[index] = { ...newData[index], is_active: isActive, ...(showSlider && { ability_value: abilityValue }) } as any;
            return newData;
        });
        try {
            const response = await updateSettings({
                scope: SETTING_SCOPE.APP,
                ability: ability,
                is_active: isActive,
                ...(showSlider && { ability_value: abilityValue }),
            }).unwrap();
            CtyShowToast(checkType.success, response?.message);
        } catch (e) {
            if (item.ability === SETTING_TYPE.DARK_MODE) {
                setDarkModeFeatureEnabled(item.is_active);
            }
            setIntegrationsData((prevData) => {
                const newData = [...prevData];
                newData[index] = { ...newData[index], is_active: item.is_active, ...(showSlider && { ability_value: defaultValue }) } as any;
                return newData;
            });
            CtyShowToast(checkType.error, `${t('Failed to update settings')}: ${(e as Error)?.message ?? 'Something went wrong!'}`);
        }
    };

    const handleUpdateLanguageOptions = async (item: any, selectedLanguages: string[] = []) => {
        const langaugeWithDefault = selectedLanguages.includes('English') ? selectedLanguages : ['English', ...selectedLanguages];
        const itemIndex = integrationsData.findIndex((data) => data.ability === item.ability);
        
        if (itemIndex === -1) return;

        try {
            const payLoad = {
                scope: SETTING_SCOPE.APP,
                ability: item.ability,
                is_active: item.is_active,
                metadata: {
                    selected_languages: langaugeWithDefault
                }
            };

            const response = await updateSettings(payLoad).unwrap();
            
            // Update local state with response data
            if (response?.data) {
                const updatedItem = Array.isArray(response.data) ? response.data[0] : response.data;
                setIntegrationsData((prevData) => {
                    const newData = [...prevData];
                    newData[itemIndex] = { ...newData[itemIndex], ...updatedItem };
                    return newData;
                });
            }
            
            CtyShowToast(checkType.success, response?.message);
        } catch (e) {
            CtyShowToast(checkType.error, `${t('Failed to update settings')}: ${(e as Error)?.message ?? 'Something went wrong!'}`);
        }
    };

    const handleUpdateCountryRegions = async (item: any, regions: AdvisorRegionSetting[]) => {
        const itemIndex = integrationsData.findIndex((data) => data.ability === item.ability);
        if (itemIndex === -1) return;

        try {
            const payLoad = {
                scope: SETTING_SCOPE.APP,
                ability: item.ability,
                is_active: item.is_active,
                metadata: {
                    ...item.metadata,
                    regions,
                },
            };

            const response = await updateSettings(payLoad).unwrap();

            if (response?.data) {
                const updatedItem = Array.isArray(response.data) ? response.data[0] : response.data;
                setIntegrationsData((prevData) => {
                    const newData = [...prevData];
                    newData[itemIndex] = { ...newData[itemIndex], ...updatedItem };
                    return newData;
                });
            }

            CtyShowToast(checkType.success, response?.message);
        } catch (e) {
            CtyShowToast(checkType.error, `${t('Failed to update settings')}: ${(e as Error)?.message ?? 'Something went wrong!'}`);
            throw e;
        }
    };

    const SettingsContentWithLoadingAndError = ErrorBoundary(SettingsIntegrationCard);

    let title: string;
    if (currentSection === 'behavior') {
        title = t('Behavior');
    } else if (currentSection === 'capabilities') {
        title = t('Capabilities');
    } else {
        title = t('Integrations and connected apps');
    }

    let description: string;
    if (currentSection === 'behavior') {
        description = t('Configure AI model parameters and response behavior.');
    } else if (currentSection === 'capabilities') {
        description = t('Enable or disable specific AI capabilities and features.');
    } else {
        description = t('Supercharge your workflow and connect the tools you use every day.');
    }

    return (
        <div className="flex flex-col h-full">
            <div className="w-full max-w-2xl flex flex-col gap-1 my-3">
                <div className="font-poppins text-lg font-semibold leading-7 text-left">{title}</div>
                <p className="font-poppins text-sm font-normal leading-5 text-left text-default-600">{description}</p>
            </div>
            <div className="flex-1 w-full max-w-2xl pb-5 flex flex-col justify-start">
                <div className="w-full">
                    <SettingsContentWithLoadingAndError
                        error={settingsError}
                        isLoading={settingsLoading || settingsFetching}
                        integrationsData={integrationsData}
                        updateIntegration={updateIntegration}
                        integrationScope={SETTING_SCOPE.APP}
                        handleUpdateLanguageOptions={handleUpdateLanguageOptions}
                        handleUpdateCountryRegions={handleUpdateCountryRegions}
                        currentSection={currentSection}
                        isDisabled={!hasIntegrationsAccess}
                    />
                </div>
            </div>
        </div>
    );
}